import Bugsnag from '@bugsnag/js';
import debug from 'debug';
const LOG = debug('analytics:snowplow');
const COLLECTOR_URL = 'https://sp-swc-web.sweatco.in';
const APP_ID = 'swc-web';
const NAMESPACE = 'sp';
const EVENT_SCHEMA = 'iglu:data.analytics/event/jsonschema/1-0-0';
// GA4's user_engagement analogue: page pings sent while the visitor is active. The first
// one lands after 10s, which is GA4's engaged-session threshold, then one every 30s.
// `activityMetrics` attaches mouse/scroll/keypress/click/touch counts to each ping.
const ACTIVITY_TRACKING = {
    minimumVisitLength: 10,
    heartbeatDelay: 30,
    activityMetrics: true,
};
const isEnabled = () => typeof SNOWPLOW_DISABLED === 'undefined' || !SNOWPLOW_DISABLED;
const browserTracker = process.browser
    ? require('@snowplow/browser-tracker')
    : null;
const wrapValue = (value) => {
    if (typeof value === 'string')
        return { string_value: value };
    if (typeof value === 'boolean')
        return { bool_value: value };
    if (typeof value === 'number') {
        return Number.isInteger(value) ? { int_value: value } : { double_value: value };
    }
    if (value === null || value === undefined)
        return { string_value: null };
    if (typeof value === 'object') {
        try {
            return { string_value: JSON.stringify(value) };
        }
        catch (_a) {
            return { string_value: '[unserializable]' };
        }
    }
    return { string_value: String(value) };
};
const toEventParams = (properties) => Object.entries(properties)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => ({ key, value: wrapValue(value) }));
const snowplowProvider = (ensureDeviceId) => {
    let userProperties = [];
    const buildEventPayload = (eventName, properties) => ({
        event_name: eventName,
        event_timestamp: Date.now(),
        event_params: toEventParams(properties),
        user_properties: userProperties,
    });
    const init = () => {
        if (!browserTracker || !isEnabled())
            return;
        try {
            browserTracker.newTracker(NAMESPACE, COLLECTOR_URL, {
                appId: APP_ID,
                plugins: [],
                cookieSameSite: 'Lax',
                stateStorageStrategy: 'cookieAndLocalStorage',
                contexts: { webPage: true, session: true },
                // Without this the tracker recomputes `refr` on every URL change, so on an SPA the
                // original external referrer is replaced by the previous internal route after the
                // first event.
                preserveOriginalReferrer: true,
            });
            // Listeners are installed on the first page view that follows this call, so enabling it
            // any later would just delay the pings by a page.
            browserTracker.enableActivityTracking(ACTIVITY_TRACKING);
            const deviceId = ensureDeviceId();
            if (deviceId)
                browserTracker.setUserId(deviceId);
            LOG('Initialized client tracker');
        }
        catch (e) {
            Bugsnag.notify(e);
        }
    };
    const trackSelfDescribing = (eventName, properties) => {
        if (!browserTracker || !isEnabled())
            return;
        try {
            browserTracker.trackSelfDescribingEvent({
                event: { schema: EVENT_SCHEMA, data: buildEventPayload(eventName, properties) },
            });
            LOG('Send event:', eventName);
        }
        catch (e) {
            Bugsnag.notify(e);
        }
    };
    const track = async (event_category, event_action, event_properties = {}) => {
        trackSelfDescribing(`${event_category}.${event_action}`.toLowerCase(), event_properties);
    };
    // GA4 emits a generic page_view next to our own named events; `trackPageView` is the
    // Snowplow equivalent, and it is what populates the native page/title columns.
    const trackNativePageView = (properties) => {
        if (!browserTracker || !isEnabled())
            return;
        try {
            const { title } = properties;
            browserTracker.trackPageView(typeof title === 'string' ? { title } : {});
            LOG('Send page_view');
        }
        catch (e) {
            Bugsnag.notify(e);
        }
    };
    const pageview = async (event_type, event_properties = {}) => {
        // Has to come first: `trackPageView` is the only call that rotates the page view id, so
        // anything sent before it on a client-side navigation still carries the previous page's.
        trackNativePageView(event_properties);
        trackSelfDescribing(event_type, event_properties);
    };
    const setUserProperties = (properties) => {
        if (!browserTracker || !isEnabled())
            return;
        userProperties = Object.entries(properties)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => ({ key, value: wrapValue(value) }));
    };
    return { init, track, pageview, setUserProperties };
};
export default snowplowProvider;
